'use client';

import { GameCardUI } from '@/features/games/shared/components/game-card-ui';
import { GameCardData } from '@/features/games/internal/types/game-card-data';
import { PriceExcludingWildMembership } from '@/shared/types/prices';
import { useIsWTGamesApp } from '@/features/games-app-bridge/shared/hooks/use-is-wt-games-app';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { useGameStore } from '@/features/games/shared/store/game-store';
import { TIME_CONSTANTS_IN_MS } from '@ecom/helper-utils';
import { mapGameCardPrices } from '@/features/games/shared/helpers/map-game-card-prices';
import { ReactNode, useEffect, useState } from 'react';
import { mapPromotionPrices } from '@/features/games/shared/helpers/map-game-prices';
import { resolveViewerPromotion } from '@/features/games/shared/helpers/resolve-viewer-promotion';
import { getEffectivePromotionalPrices } from '@/features/games/shared/helpers/get-effective-promotional-prices';
import { getPriceByType } from '@/features/games/shared/helpers/get-price-by-type';
import { filterGameCategories } from '@/features/games/shared/helpers/filter-game-categories';
import { useCurrency } from '@/features/shared/hooks/use-currency';

interface Props {
  game: GameCardData;
  imageSize?: 'tall' | 'wide' | 'square';
  showPrice?: boolean;
  showSubtitle?: boolean;
  showCategory?: boolean;
  onSale?: boolean;
  overlay?: { content?: ReactNode; ariaLabel?: string };
  showRating?: boolean;
  prefetchMode: 'full' | 'hover';
  freePriceCaption?: string;
}

export const GameCard = ({
  game,
  imageSize = 'square',
  showSubtitle = false,
  showCategory = true,
  showPrice = false,
  onSale = false,
  overlay,
  showRating,
  prefetchMode,
  freePriceCaption,
}: Props) => {
  const { name, id, image, href, prices: initialPrices, isWebGame } = game;
  const { authenticatedUser } = useUser();
  const currentCurrency = useCurrency();
  const fetchPrices = useGameStore((state) => state.fetchPrices);
  const rawPriceData = useGameStore((state) => (game.sku ? state.prices[game.sku] : undefined));

  const isMember = Boolean(authenticatedUser?.isWildMember);
  const [resolvedPriceSku, setResolvedPriceSku] = useState<string | null>(null);
  const shouldFetchPrices = showPrice && Boolean(game.sku) && !isWebGame;
  const isPriceLoading = shouldFetchPrices && resolvedPriceSku !== game.sku;

  useEffect(() => {
    if (!shouldFetchPrices || !game.sku) {
      return;
    }

    const sku = game.sku;
    let isActive = true;
    let timeoutId: number | undefined;
    let lastAttemptAt = 0;

    const scheduleNextFetch = () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      const storedTimestamp = useGameStore.getState().timestamps[sku] ?? 0;
      const now = Date.now();
      const storedIsUsable =
        storedTimestamp > 0 && now - storedTimestamp < TIME_CONSTANTS_IN_MS.TEN_MINUTES;
      const referenceTime = storedIsUsable ? storedTimestamp : lastAttemptAt;
      const msUntilStale = Math.max(0, TIME_CONSTANTS_IN_MS.TEN_MINUTES - (now - referenceTime));
      timeoutId = window.setTimeout(tick, msUntilStale);
    };

    const tick = () => {
      lastAttemptAt = Date.now();
      void fetchPrices(sku)
        .catch(() => undefined)
        .finally(() => {
          if (!isActive) return;
          setResolvedPriceSku(sku);
          scheduleNextFetch();
        });
    };

    tick();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      isActive = false;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchPrices, game.sku, shouldFetchPrices]);

  const mappedPrices = rawPriceData
    ? mapGameCardPrices(rawPriceData, {
        currencyCode: currentCurrency ?? undefined,
        isMember,
      })
    : undefined;

  const prices = mappedPrices?.length ? mappedPrices : initialPrices;

  const promotionData = resolveViewerPromotion(rawPriceData?.promotion, isMember);
  const buyOptionPromotionalPrice = getEffectivePromotionalPrices(
    promotionData && mapPromotionPrices(promotionData.prices, isMember, currentCurrency),
    prices?.find((p) => p.type === 'current')?.value,
  );
  const currentPromotionalPrice = getPriceByType(buyOptionPromotionalPrice || [], 'current');

  let price: PriceExcludingWildMembership | undefined;
  if (showPrice && prices && prices.length) {
    price = prices.find((p) => p.type === 'current');
  }
  if (price && price.value <= 0) {
    price = undefined;
  }

  const subtitle = showSubtitle && game?.brand ? game.brand : undefined;
  const categories = showCategory ? filterGameCategories(game.categories) : undefined;
  const isWTGamesApp = useIsWTGamesApp();
  const finalUrl = game.isWebGame && !isWTGamesApp ? `${href}play` : href;

  return (
    <GameCardUI
      categories={categories}
      href={finalUrl}
      id={id.toString()}
      image={image && image.src ? { src: image.src, altText: image.altText } : undefined}
      imageSize={imageSize}
      name={name}
      price={price}
      promotionPrice={freePriceCaption ? undefined : currentPromotionalPrice}
      subtitle={subtitle}
      overlay={overlay}
      hideCategories={!showCategory}
      onSale={onSale}
      rating={showRating ? game.ratings?.averageRating : undefined}
      isPriceLoading={showPrice && isPriceLoading && !price}
      freePriceCaption={freePriceCaption}
      prefetchMode={prefetchMode}
    />
  );
};
